import vtkCoordinate from '@kitware/vtk.js/Rendering/Core/Coordinate';
import vtkRenderer from '@kitware/vtk.js/Rendering/Core/Renderer';
import type vtkRenderWindow from '@kitware/vtk.js/Rendering/Core/RenderWindow';
import vtkOpenGLRenderWindow from '@kitware/vtk.js/Rendering/OpenGL/RenderWindow';

import { OrientationWidget } from './orientation-widget';
import { OverlayLayer } from './overlay-layer';
import {
    IViewport,
    ViewportOptions,
    ViewportType,
    ViewportVtkContext,
    CameraState,
    ParallelScaleRange,
} from './types';
import type { ServiceContext } from '../core/types';
import {
    DisplayFeatureType,
    FeatureAddedPayload,
    FeatureRemovedPayload,
    FeatureUpdatedPayload,
    IDisplayFeature,
    IDisplayObject,
    ViewMode,
} from '../display';
import { DisplayFeatureManager } from '../display/display-feature-manager';
import { DisplayObjectRegistry } from '../display/display-object-registry';
import { RGB, Vec3 } from '../domain';

const VIEWPORT_TYPE_TO_VIEW_MODE: Record<ViewportType, ViewMode> = {
    [ViewportType.VOLUME_3D]: ViewMode.VIEW_3D,
    [ViewportType.SLICE_AXIAL]: ViewMode.VIEW_2D_AXIAL,
    [ViewportType.SLICE_SAGITTAL]: ViewMode.VIEW_2D_SAGITTAL,
    [ViewportType.SLICE_CORONAL]: ViewMode.VIEW_2D_CORONAL,
    [ViewportType.SLICE_OBLIQUE]: ViewMode.VIEW_2D_OBLIQUE,
    [ViewportType.CPR_STRETCHED]: ViewMode.VIEW_CPR_STRETCHED,
    [ViewportType.CPR_STRAIGHTENED]: ViewMode.VIEW_CPR_STRAIGHTENED,
};

const DEFAULT_BACKGROUND_COLOR: RGB = [0.1, 0.1, 0.1];

/** Load a data URL into an HTMLImageElement, rejecting if the image fails to decode. */
function loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('Failed to load captured image'));
        img.src = src;
    });
}

/**
 * Abstract base viewport providing shared infrastructure for all viewport types.
 *
 * Shared concerns:
 * - Multi-layer renderer management
 * - Overlay canvas (OverlayLayer)
 * - DisplayObject lifecycle (subscribe to DisplayFeatureManager)
 * - canvasToWorld / worldToCanvas coordinate transforms
 * - VTK context, resize, render, dispose
 */
export abstract class BaseViewport implements IViewport {
    protected readonly renderers = new Map<number, vtkRenderer>();
    private readonly displayManager: DisplayFeatureManager;
    private readonly displayRegistry: DisplayObjectRegistry;
    private readonly viewMode: ViewMode;
    private overlayLayer!: OverlayLayer;

    /** Cached vtkCoordinate instances to avoid per-call allocations */
    protected displayToWorldCoord = vtkCoordinate.newInstance();
    protected worldToDisplayCoord = vtkCoordinate.newInstance();

    /** Map of feature ID to DisplayObject */
    private displayObjects = new Map<string, IDisplayObject>();

    /** Unsubscribe functions for event cleanup */
    protected unsubscribers: Array<() => void> = [];

    private orientationWidget: OrientationWidget | null = null;
    private disposed = false;
    private ignoreEvent = false;

    /** Background this viewport was created with — the value overrides restore to. */
    protected readonly baseBackgroundColor: RGB;

    constructor(
        readonly id: string,
        readonly type: ViewportType,
        protected readonly vtkContext: ViewportVtkContext,
        readonly context: ServiceContext,
        options?: ViewportOptions
    ) {
        // Create initial renderer in layer 0
        const renderer = this.getRenderer(0);
        this.baseBackgroundColor = options?.backgroundColor ?? DEFAULT_BACKGROUND_COLOR;
        renderer.setBackground(...this.baseBackgroundColor);

        if (options?.ignoreEvent) this.ignoreEvent = true;

        this.viewMode = VIEWPORT_TYPE_TO_VIEW_MODE[type];
        this.displayManager = context.services.getService<DisplayFeatureManager>(
            DisplayFeatureManager.SERVICE_NAME
        );
        this.displayRegistry = context.services.getService<DisplayObjectRegistry>(
            DisplayObjectRegistry.SERVICE_NAME
        );

        this.initializeCanvasOverlay();
        this.subscribeToDisplayManager();
        this.initializeExistingFeatures();

        if (options?.showOrientationWidget) {
            OrientationWidget.attach(this, () => this.disposed)
                .then((widget) => {
                    if (!widget) return;
                    this.orientationWidget = widget;
                })
                .catch((error) => {
                    console.warn(`OrientationWidget: failed to attach to viewport "${id}":`, error);
                });
        }
    }
    // ========================================================================
    // Coordinate projection
    // ========================================================================

    /**
     * Convert canvas coordinates to world coordinates.
     * VTK's vtkCoordinate handles both perspective (3D) and orthographic (2D) projection
     * automatically based on the camera's parallelProjection setting.
     */
    canvasToWorld(canvasX: number, canvasY: number, depth: number = 0): Vec3 {
        const renderer = this.getRenderer(0);
        const canvas = this.getCanvas();

        if (!canvas) return [0, 0, 0] as Vec3;

        const overlay = this.getOverlayLayer();
        const dpr = overlay.getDpr();
        const cssCoords = overlay.invertYAxisCoordinates([canvasX, canvasY]);

        this.displayToWorldCoord.setCoordinateSystemToDisplay();
        this.displayToWorldCoord.setRenderer(renderer);
        this.displayToWorldCoord.setValue([cssCoords[0] * dpr, cssCoords[1] * dpr, depth]);

        const worldCoord = this.displayToWorldCoord.getComputedWorldValue(renderer);
        return [worldCoord[0], worldCoord[1], worldCoord[2]] as Vec3;
    }

    // ========================================================================
    // DisplayFeatureManager integration
    // ========================================================================

    private subscribeToDisplayManager(): void {
        if (this.ignoreEvent) {
            return;
        }

        this.unsubscribers.push(
            this.displayManager.subscribe<FeatureAddedPayload>(
                this.displayManager.EVENTS.FEATURE_ADDED,
                ({ feature }) => this.onFeatureAdded(feature)
            )
        );

        this.unsubscribers.push(
            this.displayManager.subscribe<FeatureRemovedPayload>(
                this.displayManager.EVENTS.FEATURE_REMOVED,
                ({ id }) => this.onFeatureRemoved(id)
            )
        );

        this.unsubscribers.push(
            this.displayManager.subscribe<FeatureUpdatedPayload>(
                this.displayManager.EVENTS.FEATURE_UPDATED,
                ({ id }) => this.onFeatureUpdated(id)
            )
        );

        this.unsubscribers.push(
            this.displayManager.subscribe<FeatureUpdatedPayload>(
                this.displayManager.EVENTS.FEATURE_VISIBILITY_CHANGED,
                ({ id }) => this.onVisibilityChanged(id)
            )
        );
    }

    private initializeExistingFeatures(): void {
        const features = this.displayManager.getFeaturesVisibleIn(this.viewMode);
        for (const feature of features) {
            this.addDisplayObject(feature);
        }

        if (features.length > 0) {
            this.getRenderer(0).resetCamera();
        }
    }

    private isFeatureVisible(feature: IDisplayFeature): boolean {
        return feature.viewModeVisibility.get(this.viewMode) ?? true;
    }

    private onFeatureAdded(feature: IDisplayFeature): void {
        if (this.displayObjects.has(feature.id)) return;
        if (!this.isFeatureVisible(feature)) return;

        this.addDisplayObject(feature);
    }

    private addDisplayObject(feature: IDisplayFeature): void {
        if (this.displayObjects.has(feature.id)) return;

        const displayObject = this.displayRegistry.create(feature, this.viewMode);
        if (!displayObject) return;

        displayObject.initialize(this);
        this.displayObjects.set(feature.id, displayObject);
        this.render();
    }

    private onFeatureRemoved(id: string): void {
        const displayObject = this.displayObjects.get(id);
        if (displayObject) {
            displayObject.dispose();
            this.displayObjects.delete(id);
            this.render();
        }
    }

    private onFeatureUpdated(id: string): void {
        const displayObject = this.displayObjects.get(id);
        if (displayObject) {
            displayObject.update();
            this.render();
        }
    }

    refreshDisplayObjects(): void {
        for (const displayObject of this.displayObjects.values()) {
            displayObject.update();
        }
        this.render();
    }

    private onVisibilityChanged(id: string): void {
        const feature = this.displayManager.getFeature(id);
        if (!feature) return;

        const visible = this.isFeatureVisible(feature);
        if (visible && !this.displayObjects.has(id)) {
            this.addDisplayObject(feature);
        } else if (!visible && this.displayObjects.has(id)) {
            this.onFeatureRemoved(id);
        }
    }

    // ========================================================================
    // Overlay
    // ========================================================================

    private initializeCanvasOverlay(): void {
        this.overlayLayer = new OverlayLayer(this);

        // Apply DPR immediately so the overlay is correctly sized before the first render.
        // Without this, the overlay defaults to dpr=1 until the first external resize()
        // call, causing overlay coordinates to be in device-pixel space on high-DPI displays.
        const { container } = this.vtkContext;
        const { width, height } = container.getBoundingClientRect();
        if (width > 0 && height > 0) {
            this.overlayLayer.resize(width, height, window.devicePixelRatio || 1);
        }

        this.vtkContext.interactor.onRenderEvent(() => {
            if (this.overlayLayer) {
                this.overlayLayer.render();
            }
        });
    }

    // ========================================================================
    // Rendering and resize
    // ========================================================================

    get container(): HTMLElement {
        return this.vtkContext.container;
    }

    resize(): void {
        const { container, childView } = this.vtkContext;
        const { width, height } = container.getBoundingClientRect();

        if (width <= 0 || height <= 0) return;

        const ratio = window.devicePixelRatio || 1;
        childView.setSize(Math.floor(width * ratio), Math.floor(height * ratio));

        if (this.overlayLayer) {
            this.overlayLayer.resize(width, height, ratio);
        }
    }

    render(): void {
        this.updateLightsIfNeeded();
        this.vtkContext.render();

        if (this.overlayLayer) {
            this.overlayLayer.render();
        }
    }

    forceRender(): void {
        this.updateLightsIfNeeded();
        this.vtkContext.childRenderWindow.render();

        if (this.overlayLayer) {
            this.overlayLayer.render();
        }
    }

    private updateLightsIfNeeded(): void {
        const renderer = this.renderers.get(0);
        if (renderer && renderer.getLightFollowCamera()) {
            renderer.updateLightsGeometryToFollowCamera();
        }
    }

    // ========================================================================
    // Lifecycle
    // ========================================================================

    dispose(): void {
        this.disposed = true;
        this.orientationWidget?.dispose();
        this.orientationWidget = null;

        this.unsubscribers.forEach((unsub) => unsub());
        this.unsubscribers = [];

        this.displayToWorldCoord.delete();
        this.worldToDisplayCoord.delete();

        if (this.overlayLayer) {
            this.overlayLayer.dispose();
        }

        for (const displayObject of this.displayObjects.values()) {
            displayObject.dispose();
        }
        this.displayObjects.clear();

        this.renderers.forEach((r) => {
            this.vtkContext.childRenderWindow.removeRenderer(r);
            r.delete();
        });
        this.renderers.clear();

        this.vtkContext.interactor.delete();
        this.vtkContext.childView.setContainer(null);
    }

    // ========================================================================
    // VTK accessors
    // ========================================================================

    getRenderWindow(): vtkRenderWindow {
        return this.vtkContext.childRenderWindow;
    }

    getOpenGLRenderWindow(): vtkOpenGLRenderWindow {
        return this.vtkContext.childView;
    }

    getCanvas(): HTMLCanvasElement | null {
        const view = this.vtkContext.childView;
        if (!view || typeof view.getCanvas !== 'function') return null;
        return view.getCanvas();
    }

    async captureImage(
        format: 'image/png' | 'image/jpeg' = 'image/png',
        captureOverlay: boolean = true
    ): Promise<string | null> {
        try {
            const promises = this.vtkContext.childRenderWindow.captureImages(format);
            if (!promises || promises.length === 0) return null;

            const vtkDataUrl = await promises[0];
            if (!captureOverlay) return vtkDataUrl;

            const overlayCanvas = this.overlayLayer.getCanvas();
            if (!overlayCanvas) return vtkDataUrl;

            const vtkImage = await loadImage(vtkDataUrl);

            const dpr = this.overlayLayer.getDpr();
            const width = this.overlayLayer.getCssWidth() * dpr;
            const height = this.overlayLayer.getCssHeight() * dpr;

            // Composite the VTK frame with the overlay on a plain canvas. Using a
            // detached <canvas> + toDataURL keeps this working on browsers without
            // OffscreenCanvas/convertToBlob (e.g. Safari < 16.4).
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            if (!ctx) return null;

            ctx.drawImage(vtkImage, 0, 0, width, height);
            ctx.drawImage(overlayCanvas, 0, 0);

            return canvas.toDataURL(format);
        } catch (error) {
            console.warn(`captureImage: failed to capture viewport "${this.id}":`, error);
            return null;
        }
    }

    getRenderer(layer: number = 0): vtkRenderer {
        if (!this.renderers.has(layer)) {
            const renderer = vtkRenderer.newInstance();
            renderer.setLayer(layer);
            this.renderers.set(layer, renderer);

            this.vtkContext.childRenderWindow.addRenderer(renderer);
            this.vtkContext.childRenderWindow.setNumberOfLayers(this.renderers.size);

            if (layer > 0) {
                const defaultRenderer = this.getRenderer(0);
                renderer.setActiveCamera(defaultRenderer.getActiveCamera());
            }
        }
        return this.renderers.get(layer)!;
    }

    getAllRenderer(): vtkRenderer[] {
        return [...this.renderers.values()];
    }

    // ========================================================================
    // Coordinate transforms
    // ========================================================================

    canvasToNormalized(canvasX: number, canvasY: number): [number, number] {
        const overlay = this.getOverlayLayer();
        const w = overlay.getCssWidth();
        const h = overlay.getCssHeight();
        if (w === 0 || h === 0) return [0, 0];

        const x = (canvasX / w) * 2 - 1;
        const y = -(canvasY / h) * 2 + 1;
        return [x, y];
    }

    worldToCanvas(worldPosition: number[]): { x: number; y: number; visible: boolean } {
        const renderer = this.getRenderer(0);
        const canvas = this.getCanvas();

        if (!canvas) return { x: 0, y: 0, visible: false };

        this.worldToDisplayCoord.setCoordinateSystemToWorld();
        this.worldToDisplayCoord.setRenderer(renderer);
        this.worldToDisplayCoord.setValue(worldPosition);

        const displayCoord = this.worldToDisplayCoord.getComputedDisplayValue(renderer);
        const overlay = this.getOverlayLayer();
        const dpr = overlay.getDpr();
        const cssCoord = overlay.invertYAxisCoordinates([displayCoord[0] / dpr, displayCoord[1] / dpr]);

        const x = cssCoord[0];
        const y = cssCoord[1];
        const visible = x >= 0 && x <= overlay.getCssWidth() && y >= 0 && y <= overlay.getCssHeight();

        return { x, y, visible };
    }

    getOverlayLayer(): OverlayLayer {
        return this.overlayLayer;
    }

    updateCameraClippingRange(): void {
        this.getRenderer(0).resetCameraClippingRange();
    }

    /**
     * Zoom is unbounded by default — only viewports that know the physical
     * extent of what they display (2D slices) narrow this.
     */
    getParallelScaleRange(): ParallelScaleRange | null {
        return null;
    }

    getCameraState(): CameraState {
        const renderer = this.getRenderer(0);
        const camera = renderer.getActiveCamera();

        return {
            position: camera.getPosition() as Vec3,
            focalPoint: camera.getFocalPoint() as Vec3,
            viewUp: camera.getViewUp() as Vec3,
            viewAngle: camera.getViewAngle(),
            parallelScale: camera.getParallelScale(),
        };
    }

    setCameraState(state: CameraState): void {
        const renderer = this.getRenderer(0);
        const camera = renderer.getActiveCamera();

        camera.setPosition(...state.position);
        camera.setFocalPoint(...state.focalPoint);
        camera.setViewUp(...state.viewUp);
        camera.setViewAngle(state.viewAngle);
        renderer.resetCameraClippingRange();
        this.render();
    }

    findDisplayObjectsByFeatureType(type: DisplayFeatureType): IDisplayObject[] {
        const displayObjects: IDisplayObject[] = [];

        for (const element of this.displayObjects.values()) {
            if (element.feature.type === type) {
                displayObjects.push(element);
            }
        }

        return displayObjects;
    }
}
